<script setup>
import { reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoipeAgentEndpoints from './VoipeAgentEndpoints.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// 'twilio' (native) | 'voipe' (Asterisk/WebRTC, Smarticks)
const provider = ref('twilio');

const state = reactive({
  phoneNumber: '',
  // twilio
  accountSid: '',
  authToken: '',
  apiKeySid: '',
  apiKeySecret: '',
  // voipe
  pbxHost: '',
  wssUrl: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');
const createdVoipe = ref(null); // { id, pbxHost, wssUrl } after a voipe inbox is created

const validationRules = {
  phoneNumber: { required, isPhoneE164 },
  accountSid: { required },
  authToken: { required },
  apiKeySid: { required },
  apiKeySecret: { required },
};
const v$ = useVuelidate(validationRules, state);

const isVoipe = computed(() => provider.value === 'voipe');

const isSubmitDisabled = computed(() => {
  if (isVoipe.value) {
    return !state.phoneNumber || !state.pbxHost || !state.wssUrl;
  }
  return v$.value.$invalid;
});

const formErrors = computed(() => ({
  phoneNumber: v$.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
    : '',
  accountSid: v$.value.accountSid?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.REQUIRED')
    : '',
  authToken: v$.value.authToken?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.REQUIRED')
    : '',
  apiKeySid: v$.value.apiKeySid?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.REQUIRED')
    : '',
  apiKeySecret: v$.value.apiKeySecret?.$error
    ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.REQUIRED')
    : '',
}));

function getProviderConfig() {
  if (isVoipe.value) {
    return {
      provider: 'voipe',
      pbx_host: state.pbxHost,
      wss_url: state.wssUrl,
    };
  }
  return {
    account_sid: state.accountSid,
    auth_token: state.authToken,
    api_key_sid: state.apiKeySid,
    api_key_secret: state.apiKeySecret,
  };
}

async function createChannel() {
  if (!isVoipe.value) {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;
  } else if (isSubmitDisabled.value) {
    return;
  }

  try {
    const channel = await store.dispatch('inboxes/createVoiceChannel', {
      name: `Voice (${state.phoneNumber})`,
      voice: {
        phone_number: state.phoneNumber,
        provider: provider.value,
        provider_config: getProviderConfig(),
      },
    });

    if (isVoipe.value) {
      // stay on the page to provision agent extensions inline
      createdVoipe.value = {
        id: channel.id,
        pbxHost: state.pbxHost,
        wssUrl: state.wssUrl,
      };
      return;
    }

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE.API.ERROR_MESSAGE')
    );
  }
}

function finishVoipe() {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdVoipe.value.id },
  });
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE.DESC')"
    />

    <!-- Step 2 (voipe): provision agent extensions inline after the inbox is created -->
    <div v-if="createdVoipe" class="flex flex-col gap-5 max-w-2xl">
      <VoipeAgentEndpoints
        :inbox-id="createdVoipe.id"
        :wss-url="createdVoipe.wssUrl"
        :pbx-host="createdVoipe.pbxHost"
      />
      <div>
        <NextButton
          :label="t('VOICE_VOIPE.ENDPOINTS.CONTINUE')"
          @click="finishVoipe"
        />
      </div>
    </div>

    <form
      v-else
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <!-- provider selector -->
      <div class="flex gap-2">
        <button
          type="button"
          class="flex-1 border rounded-lg px-4 py-2.5 text-sm font-medium text-center"
          :class="
            provider === 'twilio'
              ? 'border-n-brand bg-n-brand/5 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
          @click="provider = 'twilio'"
        >
          {{ t('VOICE_VOIPE.TWILIO_LABEL') }}
        </button>
        <button
          type="button"
          class="flex-1 border rounded-lg px-4 py-2.5 text-sm font-medium text-center"
          :class="
            provider === 'voipe'
              ? 'border-n-brand bg-n-brand/5 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
          @click="provider = 'voipe'"
        >
          {{ t('VOICE_VOIPE.PROVIDER_LABEL') }}
        </button>
      </div>

      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="formErrors.phoneNumber"
        :message-type="formErrors.phoneNumber ? 'error' : 'info'"
        @blur="v$.phoneNumber?.$touch"
      />

      <!-- Voipe (Asterisk/WebRTC) fields -->
      <template v-if="isVoipe">
        <Input
          v-model="state.pbxHost"
          :label="t('VOICE_VOIPE.PBX_HOST.LABEL')"
          :placeholder="t('VOICE_VOIPE.PBX_HOST.PLACEHOLDER')"
        />
        <Input
          v-model="state.wssUrl"
          :label="t('VOICE_VOIPE.WSS_URL.LABEL')"
          :placeholder="t('VOICE_VOIPE.WSS_URL.PLACEHOLDER')"
          :message="t('VOICE_VOIPE.WSS_URL.HELP')"
          message-type="info"
        />
      </template>

      <!-- Twilio fields -->
      <template v-else>
        <Input
          v-model="state.accountSid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.PLACEHOLDER')
          "
          :message="formErrors.accountSid"
          :message-type="formErrors.accountSid ? 'error' : 'info'"
          @blur="v$.accountSid?.$touch"
        />
        <Input
          v-model="state.authToken"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.PLACEHOLDER')"
          :message="formErrors.authToken"
          :message-type="formErrors.authToken ? 'error' : 'info'"
          @blur="v$.authToken?.$touch"
        />
        <Input
          v-model="state.apiKeySid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.PLACEHOLDER')
          "
          :message="formErrors.apiKeySid"
          :message-type="formErrors.apiKeySid ? 'error' : 'info'"
          @blur="v$.apiKeySid?.$touch"
        />
        <Input
          v-model="state.apiKeySecret"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.PLACEHOLDER')
          "
          :message="formErrors.apiKeySecret"
          :message-type="formErrors.apiKeySecret ? 'error' : 'info'"
          @blur="v$.apiKeySecret?.$touch"
        />
      </template>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
